import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

const communicationToCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    clientId: obj?.client_id,
    projectId: obj?.project_id,
    userId: obj?.user_id,
    communicationType: obj?.communication_type,
    subject: obj?.subject,
    message: obj?.message,
    durationMinutes: obj?.duration_minutes,
    attachments: obj?.attachments || [],
    metadata: obj?.metadata,
    createdAt: obj?.created_at,
  };
};

const communicationToSnakeCase = (obj) => {
  return {
    client_id: obj?.clientId,
    project_id: obj?.projectId,
    user_id: obj?.userId,
    communication_type: obj?.communicationType,
    subject: obj?.subject,
    message: obj?.message,
    duration_minutes: obj?.durationMinutes,
    attachments: obj?.attachments,
    metadata: obj?.metadata,
  };
};

const satisfactionToCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    clientId: obj?.client_id,
    projectId: obj?.project_id,
    overallScore: obj?.overall_score,
    serviceQualityScore: obj?.service_quality_score,
    communicationScore: obj?.communication_score,
    timelinessScore: obj?.timeliness_score,
    valueScore: obj?.value_score,
    feedback: obj?.feedback,
    wouldRecommend: obj?.would_recommend,
    createdAt: obj?.created_at,
  };
};

const satisfactionToSnakeCase = (obj) => {
  return {
    client_id: obj?.clientId,
    project_id: obj?.projectId,
    overall_score: obj?.overallScore,
    service_quality_score: obj?.serviceQualityScore,
    communication_score: obj?.communicationScore,
    timeliness_score: obj?.timelinessScore,
    value_score: obj?.valueScore,
    feedback: obj?.feedback,
    would_recommend: obj?.wouldRecommend,
  };
};

const createClient = async (clientData) => {
  const { data, error } = await supabase?.from('clients')?.insert([clientData])?.select()?.single();

  if (error) throw error;
  return data;
};

const updateClient = async (id, clientData) => {
  const { data, error } = await supabase?.from('clients')?.update(clientData)?.eq('id', id)?.select()?.single();

  if (error) throw error;
  return data;
};

const deleteClient = async (id) => {
  const { error } = await supabase?.from('clients')?.delete()?.eq('id', id);

  if (error) throw error;
};

export const crmService = {
  // Get all leads with optional filters
  async getAllLeads(filters = {}) {
    let query = supabase
      ?.from('leads')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.segment) {
      query = query?.eq('segment', filters?.segment);
    }

    if (filters?.assignedTo) {
      query = query?.eq('assigned_to', filters?.assignedTo);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Client Communications
  async getCommunications(clientId) {
    const { data, error } = await supabase
      ?.from('client_communications')
      ?.select('*, user_profiles(full_name, avatar)')
      ?.eq('client_id', clientId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(communicationToCamelCase) || [];
  },

  async createCommunication(communication) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('client_communications')
      ?.insert({
        ...communicationToSnakeCase(communication),
        user_id: user?.id,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return communicationToCamelCase(data);
  },

  // Client Satisfaction
  async getSatisfactionScores(clientId) {
    const { data, error } = await supabase
      ?.from('client_satisfaction')
      ?.select('*')
      ?.eq('client_id', clientId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(satisfactionToCamelCase) || [];
  },

  async createSatisfactionScore(satisfaction) {
    const { data, error } = await supabase
      ?.from('client_satisfaction')
      ?.insert(satisfactionToSnakeCase(satisfaction))
      ?.select()
      ?.single();

    if (error) throw error;
    return satisfactionToCamelCase(data);
  },

  // Client Segmentation & Analytics
  async getClientsBySegment(segment) {
    let query = supabase
      ?.from('leads')
      ?.select('*')
      ?.order('lifetime_value', { ascending: false });

    if (segment && segment !== 'all') {
      query = query?.eq('segment', segment);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async updateClientSegment(clientId, segment) {
    const { data, error } = await supabase
      ?.from('leads')
      ?.update({ segment })
      ?.eq('id', clientId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  async getClientHistory(clientId) {
    const [quotes, projects, communications, satisfaction] = await Promise.all([
      supabase?.from('quotes')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false }),
      supabase?.from('projects')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false }),
      this.getCommunications(clientId),
      this.getSatisfactionScores(clientId),
    ]);

    return {
      quotes: quotes?.data || [],
      projects: projects?.data || [],
      communications: communications || [],
      satisfaction: satisfaction || [],
    };
  },

  async scheduleFollowUp(clientId, followUpDate, notes) {
    const { data, error } = await supabase
      ?.from('leads')
      ?.update({
        next_follow_up: followUpDate,
        notes: notes,
      })
      ?.eq('id', clientId)
      ?.select()
      ?.single();

    if (error) throw error;
    
    // Send follow-up reminder email
    try {
      if (data?.email && data?.name) {
        await emailService?.sendFollowUpReminder(
          data?.email,
          data?.name,
          'Rappel de suivi',
          notes || 'Nous souhaitions prendre de vos nouvelles.'
        );
      }
    } catch (emailError) {
      console.error('Failed to send follow-up reminder:', emailError);
    }
    
    return data;
  },

  async sendFollowUpForClient(clientId, subject, message, quoteNumber = null, projectNumber = null) {
    try {
      const { data: clientData } = await supabase
        ?.from('leads')
        ?.select('name, email')
        ?.eq('id', clientId)
        ?.single();
      
      if (!clientData?.email) {
        throw new Error('Client email not found');
      }
      
      await emailService?.sendFollowUpReminder(
        clientData?.email,
        clientData?.name,
        subject,
        message,
        quoteNumber,
        projectNumber
      );
      
      return { success: true };
    } catch (error) {
      console.error('Failed to send follow-up:', error);
      throw error;
    }
  },

  async getUpcomingFollowUps() {
    const { data, error } = await supabase
      ?.from('leads')
      ?.select('*')
      ?.not('next_follow_up', 'is', null)
      ?.gte('next_follow_up', new Date()?.toISOString())
      ?.order('next_follow_up', { ascending: true })
      ?.limit(10);

    if (error) throw error;
    return data || [];
  },

  subscribeToCommunications(callback) {
    const channel = supabase
      ?.channel('client_communications')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'client_communications' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },

  createClient,
  updateClient,
  deleteClient,
};